/**
 * Canonical list of all entity names in the InstantDB schema.
 *
 * The string values match:
 *   1. The entity names declared in instant.schema.ts
 *   2. The localStorage store keys used by usePersistentMockState
 *   3. The keys cleared by clearAllData() in lib/export/bizopData.ts
 *
 * Keeping them aligned means the dual-backed hook can use the same key to
 * look up data in either backend.
 */

/** All entity (= store key) names known to the BizOP admin app. */
export const DB_ENTITY_NAMES = [
  // Sales
  "dealers",
  "dealerTiers",
  "salesOrders",
  "salesOrderLines",
  "salesOrderStatusHistory",
  "dealerOffers",

  // Invoicing
  "invoices",
  "invoiceLines",
  "payments",
  "creditNotes",

  // Catalog
  "brands",
  "categories",
  "units",
  "taxRates",
  "products",
  "productVariants",
  "boms",
  "bomLines",

  // Inventory
  "branches",
  "warehouses",
  "stockMovements",
  "stockReservations",

  // Workforce
  "employees",
  "attendance",
  "leaves",
  "employeePayments",

  // Finance
  "loans",
  "loanRepayments",
  "expenses",
  "fxRates",

  // GL
  "accounts",
  "journalEntries",
  "journalLines",

  // Payables
  "supplierPayments",
  "supplierInvoices",
  "suppliers",

  // Pricing
  "priceLists",
  "priceListItems",
  "dealerPriceListAssignments",

  // Dealer areas
  "dealerAreas",

  // Production
  "workOrders",
  "workOrderConsumptions",
  "workOrderProductions",

  // Imports
  "lettersOfCredit",
  "purchaseOrders",
  "shipments",

  // Sales Operations
  "returns",
  "deliveries",

  // Inventory transfers
  "stockTransfers",

  // Settings singletons
  "companyProfile",

  // Notifications / attachments / audit / roles
  "notifications",
  "attachments",
  "auditEntries",
  "roles",
  "officeUsers",
] as const;

export type DbEntityName = (typeof DB_ENTITY_NAMES)[number];

/** Type guard — does this string match a known entity name? */
export function isDbEntityName(s: string): s is DbEntityName {
  return (DB_ENTITY_NAMES as readonly string[]).includes(s);
}

/**
 * Store keys that aren't 1:1 with an entity. Used by the dual-backed
 * usePersistentMockState to know which keys to ignore (i.e. keep on
 * localStorage even when InstantDB is enabled).
 *
 * Most onboarding state and a few UI prefs stay local — they aren't
 * cross-user data and don't benefit from sync.
 */
export const LOCAL_ONLY_STORE_KEYS = new Set<string>([
  "onboardingComplete",
  "openingBalance",
  "companySetup",
  "warehouseSetup",
]);
